/* Isolated V1. Native scrolling replaces the legacy touch-preview timer; AYAQ's
 * color/media ordering and first/second-image view modes are kept in Liquid. */
(() => {
  class PremiumCard extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.events = new window.AbortController();
      this.addEventListener('click', (event) => this.onClick(event), {
        signal: this.events.signal,
      });
      const warmColor = (event) => {
        const swatch = event.target.closest('.ayaq-premium-card__swatch');
        const template = this.colorTemplate(swatch?.dataset.colorKey);
        if (template) this.prepareColor(template);
      };
      this.addEventListener('pointerover', warmColor, {
        signal: this.events.signal,
      });
      this.addEventListener('focusin', warmColor, {
        signal: this.events.signal,
      });
      this.connectGallery();
      this.updateCompactColors();
      if (this.querySelector('.ayaq-premium-card__more-colors')) {
        this.colorObserver = new ResizeObserver(() =>
          this.updateCompactColors(),
        );
        this.colorObserver.observe(this);
      }
    }

    disconnectedCallback() {
      this.events?.abort();
      this.events = null;
      this.preparedColors = null;
      this.colorObserver?.disconnect();
      this.disconnectGallery();
    }

    disconnectGallery() {
      this.updateRequest = null;
      this.pendingIndex = null;
      this.galleryEvents?.abort();
      this.imageObserver?.disconnect();
      this.sizeObserver?.disconnect();
      cancelAnimationFrame(this.scrollFrame);
    }

    connectGallery(readyIndex) {
      this.disconnectGallery();
      this.gallery = this.querySelector('[data-gallery]');
      if (!this.gallery) return;
      this.slides = Array.from(this.gallery.querySelectorAll('[data-slide]'));
      this.galleryWidth = this.gallery.clientWidth;
      this.index = 0;
      this.manual = false;
      this.galleryEvents = new window.AbortController();
      const options = { signal: this.galleryEvents.signal, passive: true };
      const media = this.gallery.closest('.ayaq-premium-card__media');
      media.addEventListener(
        'pointerenter',
        () => this.preloadAdjacent(),
        options,
      );
      media.addEventListener('focusin', () => this.preloadAdjacent(), options);
      this.gallery.addEventListener(
        'scroll',
        () => {
          cancelAnimationFrame(this.scrollFrame);
          this.scrollFrame = requestAnimationFrame(() => this.syncSlide());
        },
        options,
      );
      this.gallery.addEventListener(
        'pointerdown',
        () => {
          this.manual = true;
          this.updateRequest = null;
          this.pendingIndex = null;
          this.preloadAdjacent();
        },
        options,
      );
      this.imageObserver = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.intersectionRatio > 0) {
              this.hydrate(this.slides.indexOf(entry.target));
            }
          }
        },
        { root: this.gallery, threshold: 0.01 },
      );
      this.slides.forEach((slide) => this.imageObserver.observe(slide));
      this.sizeObserver = new ResizeObserver(() => {
        const width = this.gallery.clientWidth;
        if (width === this.galleryWidth) return;
        this.galleryWidth = width;
        this.updateSizes();
        this.gallery.scrollTo({
          left: this.index * this.gallery.clientWidth,
          behavior: 'auto',
        });
      });
      this.sizeObserver.observe(this.gallery);
      if (readyIndex !== undefined) {
        this.gallery.scrollLeft = readyIndex * this.gallery.clientWidth;
      }
      this.syncSlide();
      if (readyIndex === undefined && this.viewIndex() > 0) {
        this.goTo(this.viewIndex(), false);
      }
    }

    viewIndex() {
      const mode =
        this.closest('ayaq-premium-collection')?.dataset.viewMode || 'model';
      return Number(this.gallery?.dataset[`${mode}Index`] || 0);
    }

    preloadAdjacent() {
      if (this.slides.length < 2) return;
      for (const direction of [-1, 1]) {
        const index =
          (this.index + direction + this.slides.length) % this.slides.length;
        this.hydrate(index, 'low');
      }
    }

    hydrate(index, priority = 'auto') {
      const slide = this.slides[index];
      const template = slide?.querySelector('template[data-slide-image]');
      if (!template) return slide?.querySelector('img');
      const fragment = template.content.cloneNode(true);
      const image = fragment.querySelector('img');
      if (image) {
        image.loading = 'eager';
        image.fetchPriority = priority;
        image.sizes = `${Math.ceil(this.gallery.clientWidth)}px`;
      }
      template.replaceWith(fragment);
      return image;
    }

    updateSizes() {
      const width = Math.ceil(this.gallery.clientWidth);
      if (!width) return;
      this.gallery.querySelectorAll('img').forEach((image) => {
        image.sizes = `${width}px`;
      });
    }

    syncSlide() {
      if (!this.gallery.clientWidth) return;
      const nextIndex = Math.round(
        Math.abs(this.gallery.scrollLeft) / this.gallery.clientWidth,
      );
      this.index = Math.min(this.slides.length - 1, Math.max(0, nextIndex));
      this.hydrate(this.index);
      this.slides.forEach((slide, index) => {
        slide.tabIndex = index === this.index ? 0 : -1;
      });
      this.querySelectorAll('[data-gallery-dot]').forEach((dot, index) => {
        dot.toggleAttribute('data-active', index === this.index);
      });
      const counter = this.querySelector('[data-gallery-counter]');
      if (counter) {
        counter.textContent = `${this.index + 1} / ${this.slides.length}`;
        counter.setAttribute(
          'aria-label',
          counter.dataset.label.replace('__CURRENT__', this.index + 1),
        );
      }
    }

    async goTo(index, animate = true) {
      if (!this.slides?.length) return;
      const request = {};
      this.updateRequest = request;
      const target = Math.min(Math.max(0, index), this.slides.length - 1);
      this.pendingIndex = target;
      const image = this.hydrate(target);
      if (image) {
        image.fetchPriority = 'auto';
        try {
          await image.decode();
        } catch {
          if (this.updateRequest === request) this.pendingIndex = null;
          return; // Keep the current image if the next image fails to load.
        }
      }
      if (this.updateRequest !== request || !this.isConnected) return;
      this.pendingIndex = null;
      const reduced = window.matchMedia(
        '(prefers-reduced-motion: reduce)',
      ).matches;
      // A wrapped/jumped move must not scroll through unloaded intermediate slides.
      const smooth = animate && !reduced && Math.abs(target - this.index) === 1;
      this.gallery.scrollTo({
        left: target * this.gallery.clientWidth,
        behavior: smooth ? 'smooth' : 'auto',
      });
      this.index = target;
      if (!smooth) this.syncSlide();
      if (this.manual) this.preloadAdjacent();
    }

    colorTemplate(key) {
      if (!key) return null;
      return Array.from(
        this.querySelectorAll('template[data-color-template]'),
      ).find((item) => item.dataset.colorTemplate === key);
    }

    prepareColor(template) {
      this.preparedColors ||= new WeakMap();
      const mode =
        this.closest('ayaq-premium-collection')?.dataset.viewMode || 'model';
      const width = Math.ceil(this.gallery.clientWidth);
      const cached = this.preparedColors.get(template);
      if (cached?.mode === mode && cached.width === width) return cached;
      // Import into the active document so responsive images can load/decode
      // while this color remains detached and the current color stays visible.
      const fragment = document.importNode(template.content, true);
      const gallery = fragment.querySelector('[data-gallery]');
      const index = Number(gallery.dataset[`${mode}Index`] || 0);
      const slide = gallery.querySelectorAll('[data-slide]')[index];
      const deferred = slide.querySelector('template[data-slide-image]');
      if (deferred) deferred.replaceWith(deferred.content.cloneNode(true));
      fragment.querySelectorAll('img').forEach((image) => {
        image.loading = 'eager';
        image.fetchPriority = 'low';
        image.sizes = `${width}px`;
      });
      const image = slide.querySelector('img');
      const ready = image
        ? image.decode().then(
            () => true,
            () => {
              if (this.preparedColors?.get(template) === prepared) {
                this.preparedColors.delete(template);
              }
              return false;
            },
          )
        : Promise.resolve(true);
      const prepared = { fragment, mode, width, index, ready };
      this.preparedColors.set(template, prepared);
      return prepared;
    }

    updateCompactColors() {
      const swatches = Array.from(
        this.querySelectorAll('.ayaq-premium-card__swatch'),
      );
      if (!this.querySelector('.ayaq-premium-card__more-colors')) return;
      // Keep 44px touch targets: narrow cards show two colors plus the control.
      const limit = this.clientWidth >= 184 ? 3 : 2;
      const selected = swatches.find((swatch) =>
        swatch.hasAttribute('aria-current'),
      );
      const visible = swatches.slice(0, limit);
      if (selected && !visible.includes(selected)) {
        visible[limit - 1] = selected;
      }
      swatches.forEach((swatch) =>
        swatch.toggleAttribute('data-extra-color', !visible.includes(swatch)),
      );
    }

    async onClick(event) {
      const more = event.target.closest('.ayaq-premium-card__more-colors');
      if (more) {
        const expanded = this.toggleAttribute('data-colors-expanded');
        more.setAttribute('aria-expanded', String(expanded));
        return;
      }
      const arrow = event.target.closest('[data-direction]');
      if (arrow) {
        event.preventDefault();
        this.manual = true;
        const next =
          ((this.pendingIndex ?? this.index) +
            Number(arrow.dataset.direction) +
            this.slides.length) %
          this.slides.length;
        this.goTo(next);
        return;
      }
      const swatch = event.target.closest(
        '[data-color-key].ayaq-premium-card__swatch',
      );
      if (
        !swatch ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey ||
        event.button > 0
      ) {
        return;
      }
      const key = swatch.dataset.colorKey;
      const panel = this.querySelector('[data-color-panel]');
      const request = {};
      this.updateRequest = request;
      this.pendingIndex = null;
      if (panel?.dataset.colorKey === key) {
        event.preventDefault();
        return;
      }
      const template = this.colorTemplate(key);
      if (!template || !panel) return; // Real variant links remain the fallback.
      event.preventDefault();
      const prepared = this.prepareColor(template);
      if (
        !(await prepared.ready) ||
        this.updateRequest !== request ||
        !this.isConnected
      ) {
        return;
      }
      const saved = document.createElement('template');
      saved.dataset.colorTemplate = panel.dataset.colorKey;
      saved.content.append(
        ...Array.from(panel.childNodes).map((node) => node.cloneNode(true)),
      );
      this.append(saved);
      panel.replaceChildren(prepared.fragment);
      panel.dataset.colorKey = key;
      template.remove();
      this.preparedColors.delete(template);
      this.querySelectorAll('.ayaq-premium-card__swatch').forEach((item) => {
        if (item === swatch) item.setAttribute('aria-current', 'true');
        else item.removeAttribute('aria-current');
      });
      this.connectGallery(prepared.index);
      this.updateCompactColors();
    }
  }

  class PremiumCollection extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.events = new window.AbortController();
      const options = { capture: true, signal: this.events.signal };
      this.preview = new window.URL(window.location.href).searchParams.get(
        'preview_theme_id',
      );
      let mode = 'model';
      try {
        mode = window.localStorage.getItem('ayaq_view_mode') || mode;
      } catch {
        /* Storage is optional. */
      }
      this.setView(mode, false);
      this.syncContext();
      this.addEventListener('input', () => this.syncContext(), options);
      this.addEventListener(
        'click',
        (event) => {
          const link = event.target.closest('a[href]');
          if (link) this.contextualize(link);
          const quick = event.target.closest('[data-premium-quick-add]');
          if (
            quick &&
            !event.metaKey &&
            !event.ctrlKey &&
            !event.shiftKey &&
            !event.altKey &&
            event.button === 0 &&
            window.matchMedia(
              '(min-width: 990px) and (hover: hover) and (pointer: fine)',
            ).matches
          ) {
            const modal = this.querySelector('ayaq-premium-quick-add');
            if (modal?.show) {
              event.preventDefault();
              modal.show(quick);
            }
            return;
          }
          const view = event.target.closest('[data-premium-view]');
          if (!view) return;
          event.preventDefault();
          this.setView(view.dataset.premiumView);
        },
        options,
      );
      this.rowObserver = new ResizeObserver(() => this.scheduleRowAlignment());
      const results = this.querySelector('#ProductGridContainer');
      this.observer = new MutationObserver((records) => {
        if (records.some((record) => record.target === results)) {
          this.syncContext();
        }
        // Gallery counters/images change during navigation but cannot change
        // the text rows. Only rebuild observations when cards/content change.
        if (
          records.some(
            ({ target }) =>
              target === results ||
              target.matches?.(
                '.ayaq-premium__grid, ayaq-premium-card, [data-color-panel], .ayaq-premium-card__description',
              ),
          )
        ) {
          this.observeRows();
        }
      });
      if (results) {
        this.observer.observe(results, { childList: true, subtree: true });
      }
      this.observeRows();
    }

    disconnectedCallback() {
      this.events?.abort();
      this.events = null;
      this.observer?.disconnect();
      this.rowObserver?.disconnect();
      window.cancelAnimationFrame(this.rowFrame);
      this.rowFrame = null;
    }

    observeRows() {
      this.rowObserver.disconnect();
      this.querySelectorAll(
        '.ayaq-premium__grid, .ayaq-premium-card__title, .ayaq-premium-card__description, .ayaq-premium-card__sizes, .ayaq-premium-card__swatches, .ayaq-premium-card__price',
      ).forEach((element) => this.rowObserver.observe(element));
      this.scheduleRowAlignment();
    }

    scheduleRowAlignment() {
      if (this.rowFrame) return;
      this.rowFrame = window.requestAnimationFrame(() => {
        this.rowFrame = null;
        this.alignRows();
      });
    }

    alignRows() {
      if (!this.isConnected) return;
      const cards = Array.from(this.querySelectorAll('ayaq-premium-card'));
      // Reset together before measuring so rows can shrink after a resize,
      // color change or filtering. CSS keeps all copy readable without JS.
      cards.forEach((card) => {
        card.style.removeProperty('--premium-heading-height');
        card.style.removeProperty('--premium-details-height');
      });
      if (!window.matchMedia('(min-width: 990px)').matches) return;
      const rows = new Map();
      cards.forEach((card) => {
        const media = card.querySelector('.ayaq-premium-card__media');
        const details = card.querySelector('.ayaq-premium-card__details');
        if (!media || !details) return;
        const top = Math.round(card.getBoundingClientRect().top);
        const rect = details.getBoundingClientRect();
        const row = rows.get(top) || { cards: [], heading: 0, details: 0 };
        row.cards.push(card);
        row.heading = Math.max(
          row.heading,
          rect.top - media.getBoundingClientRect().bottom,
        );
        row.details = Math.max(row.details, rect.height);
        rows.set(top, row);
      });
      rows.forEach((row) => {
        row.cards.forEach((card) => {
          card.style.setProperty(
            '--premium-heading-height',
            `${row.heading}px`,
          );
          card.style.setProperty(
            '--premium-details-height',
            `${row.details}px`,
          );
        });
      });
    }

    setView(mode, persist = true) {
      this.dataset.viewMode = mode === 'product' ? 'product' : 'model';
      this.querySelectorAll('[data-premium-view]').forEach((button) => {
        button.setAttribute(
          'aria-pressed',
          String(button.dataset.premiumView === this.dataset.viewMode),
        );
      });
      this.querySelectorAll('ayaq-premium-card').forEach((card) => {
        if (!card.gallery) return;
        card.manual = false;
        card.goTo(card.viewIndex(), false);
      });
      if (persist) {
        try {
          window.localStorage.setItem('ayaq_view_mode', this.dataset.viewMode);
        } catch {
          /* Storage is optional. */
        }
      }
    }

    contextualize(link) {
      const url = new window.URL(link.href, window.location.href);
      if (
        url.origin !== window.location.origin ||
        url.pathname !== window.location.pathname
      ) {
        return;
      }
      if (this.dataset.view) url.searchParams.set('view', this.dataset.view);
      if (this.preview) url.searchParams.set('preview_theme_id', this.preview);
      link.href = url.pathname + url.search + url.hash;
    }

    syncContext() {
      this.querySelectorAll('.ayaq-premium__facets form').forEach((form) => {
        for (const [name, value] of [
          ['view', this.dataset.view],
          ['preview_theme_id', this.preview],
        ]) {
          if (!value) continue;
          let input = form.querySelector(`input[name="${name}"]`);
          if (!input) {
            input = document.createElement('input');
            input.type = 'hidden';
            input.name = name;
            form.append(input);
          }
          input.value = value;
        }
      });
      this.querySelectorAll(
        '.ayaq-premium__facets a[href], .pagination a[href], .ayaq-premium__empty a[href]',
      ).forEach((link) => this.contextualize(link));
    }
  }

  if (!customElements.get('ayaq-premium-card')) {
    customElements.define('ayaq-premium-card', PremiumCard);
  }
  if (!customElements.get('ayaq-premium-collection')) {
    customElements.define('ayaq-premium-collection', PremiumCollection);
  }
})();
